/**
 * Navigation Helpers - Utilities for managing navigation favorites
 *
 * Provides functions for:
 * - Generating unique item IDs from navigation routes
 * - Organizing favorites by section/category
 * - Mapping navigation structure for favorites
 */

// Note: FavoriteNavigationItem is defined in AuthContext.tsx to avoid circular dependencies
// Import it from there when needed

export interface NavigationItem {
  id: string;
  name: string;
  route: string;
  icon?: string;
  section: string;
  category?: string;
}

// Generic favorite item interface for type safety in helper functions
export interface FavoriteItem {
  id: number;
  item_id: string;
  name: string;
  route: string;
  icon: string;
  section: string;
  category: string;
  order: number;
  created_at: string;
}

export interface FavoritesBySection {
  [section: string]: {
    items: FavoriteItem[];
    categories: {
      [category: string]: FavoriteItem[];
    };
  };
}

/**
 * Generate a unique item_id from route and name
 * Format: route-based identifier (e.g., "/assessments" -> "assessments")
 */
export const generateItemId = (route: string, name: string): string => {
  // Remove leading slash and replace slashes with hyphens
  const routePart = route.replace(/^\//, '').replace(/\//g, '-') || 'root';
  // Convert name to kebab-case
  const namePart = name.toLowerCase().replace(/\s+/g, '-');
  return `${routePart}-${namePart}`;
};

/**
 * Extract section name from a navigation item
 * For nested items, this would be the top-level section
 */
export const extractSection = (menuId: string, label: string): string => {
  // Convert menu ID to readable section name
  const sectionMap: { [key: string]: string } = {
    dashboard: 'Dashboard',
    schools: 'Schools',
    users: 'Users',
    assessments: 'Assessments',
    teacher_tools: 'Teacher Tools',
    pat: 'PAT Assessments',
    privacy: 'Privacy',
  };

  return sectionMap[menuId] || label;
};

/**
 * Organize favorites by section and optionally by category
 */
export const organizeFavoritesBySection = <T extends FavoriteItem>(
  favorites: T[]
): FavoritesBySection => {
  const organized: FavoritesBySection = {};

  favorites.forEach((favorite) => {
    const section = favorite.section;

    // Initialize section if not exists
    if (!organized[section]) {
      organized[section] = {
        items: [],
        categories: {},
      };
    }

    // Add to section items
    organized[section].items.push(favorite);

    // If has category, also organize by category
    if (favorite.category) {
      if (!organized[section].categories[favorite.category]) {
        organized[section].categories[favorite.category] = [];
      }
      organized[section].categories[favorite.category].push(favorite);
    }
  });

  return organized;
};

/**
 * Check if a navigation item is favorited
 */
export const isFavorited = <T extends FavoriteItem>(
  itemId: string,
  favorites: T[]
): boolean => {
  return favorites.some((fav) => fav.item_id === itemId);
};

/**
 * Find a favorite by item_id
 */
export const findFavorite = <T extends FavoriteItem>(
  itemId: string,
  favorites: T[]
): T | undefined => {
  return favorites.find((fav) => fav.item_id === itemId);
};

/**
 * Get favorites for a specific section
 */
export const getFavoritesBySection = <T extends FavoriteItem>(
  section: string,
  favorites: T[]
): T[] => {
  return favorites.filter((fav) => fav.section === section);
};

/**
 * Sort favorites by order, then by created_at
 */
export const sortFavorites = <T extends FavoriteItem>(
  favorites: T[]
): T[] => {
  return [...favorites].sort((a, b) => {
    if (a.order !== b.order) {
      return a.order - b.order;
    }
    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  });
};

/**
 * Create a navigation item object from menu data
 */
export const createNavigationItem = (
  label: string,
  route: string,
  section: string,
  icon?: string,
  category?: string
): NavigationItem => {
  const itemId = generateItemId(route, label);

  return {
    id: itemId,
    name: label,
    route,
    icon: icon || '',
    section,
    category: category || '',
  };
};

/**
 * Get all sections from favorites
 */
export const getAllSections = <T extends FavoriteItem>(
  favorites: T[]
): string[] => {
  const sections = new Set<string>();
  favorites.forEach((fav) => sections.add(fav.section));
  return Array.from(sections).sort();
};

/**
 * Count favorites by section
 */
export const countFavoritesBySection = <T extends FavoriteItem>(
  favorites: T[]
): { [section: string]: number } => {
  const counts: { [section: string]: number } = {};
  favorites.forEach((fav) => {
    counts[fav.section] = (counts[fav.section] || 0) + 1;
  });
  return counts;
};
